/**
  * @class mmodOfficeWfDashboard
  * @description
  * Dashboard module provides overview of current operational situation for office users.
  * It shows graphs displaying predefined values of some system characteristics:
  * - available vehicles: shows vehicles with running mBox
  * - available users: shows online/offline users
  * - operational status: displays operational status of users of mPatrols
  * - ALPR severity: displays today’s license plate recognition split by their severity
  * - ALPR severity: displays today’s license plate recognition split by their priority
  * Module is implemented as workflow wfDashboard. Server class is msvrWfDashboard.
  * msvrWfDashboard at it’s start statically defines the reports in constructor
  * - each report is just SQL query returning several values to display in each circle graph.
  * After start server service regularly performs SQL query for each report and if the values are changed,
  * it performs CALCULATE action on workflow object for the report.
  * This will inform all workflow watchers in mOffice applications to receive an update and redraw their graphs.
  * On client side, there is mmodOfficeWfDashboard module, which is derived from mmodWorkflow.
  * It interprets incoming report data and transforms them to visual graphs using chart.js library.
  */

import mmodModule from 'mmodModule'
import Chart from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import doughnutlabel from 'chartjs-plugin-doughnutlabel'

const default_charts = {
  available_vehicles: {
    labels: ['ONLINE', 'OFFLINE'],
    colors: ['green', 'gray'],
    type: 'doughnut',
  },
  available_users: {
    labels: ['ONLINE', 'OFFLINE'],
    colors: ['green', 'gray'],
    type: 'doughnut',
  },
  mpatrol_status: {
    labels: ['bpause', 'gready', 'opatrol', 'rbusy'],
    colors: ['gray', 'green', 'orange', 'red'],
    type: 'doughnut',
  },
  alpr_severity: {
    labels: ['1', '2'],
    colors: ['orange', 'red'],
    type: 'doughnut',
  },
  alpr_hits: {
    labels: ['0', '1'],
    colors: ['gray', 'red'],
    type: 'doughnut',
  },
}

export default class mmodOfficeWfDashboard extends mmodModule {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([/* put module specific dependencies here */], dependencies)
    cfg_schema = _.extend({
      init: { type: 'object', default: { type: 'full' }, description: 'Initial window setup; same structure as required by `mmWindowSet`' },
      workflow_server: { type: 'string', description: 'Listener acting as the server-side for the workflow', default: ':msvrWfDashboard' },
      charts: { type: 'object', description: 'chart definitions', default: default_charts },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * initialization of GUI components
  */
  initWindow() {
    this.loadForm(require('./mmodOfficeWfDashboard.json'))
    this.elCharts = this.el.find('>mele-body >@charts')
    this.charts = {}

    this.TEMPLATES = this.config.charts
    for(const template in this.TEMPLATES) {
      this.initChart(template)
    }
    this.mmStart()
    this.subscribe('#WM')
  } // initWindow

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * information on changed user authorization; user_id is empty if not logged
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {integer} [params.user_id] - user's unique ID
  * @param {string} [params.login] - user's login name
  * @param {string} [params.callsign] - user's callsign
  * @param {object} [params.roles] - roles assigned to user
  * @param {string} [params.privatekey] - user's private key encrypted by his password
  * @param {string} [params.error] - error message if login failed
  */
  mmUserLogged(sender, params) {
    super.mmUserLogged(sender, params)
    this.watchReports(true)
  } // mmUserLogged

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * used to return list of workflow processes
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.full] - whether it is full or incremental update
  * @param {boolean} [params.limited] - indicates if data were limited to some fields only
  * @param {integer} [params.version] - version of data sent by server
  * @param {array} [params.sort] - sort criteria
  * @param {object} [params.query] - query parameters
  * @param {string} [params.type] - type of returned objects, e.g. id of workflow producing the objects
  * @param {integer} [params.total] - total number of objects matching the query
  * @param {integer} [params.offset] - offset of returned objects
  * @param {integer} [params.limit] - limits used in the query
  * @param {array} params.list - array of matching objects
  */
  mmObjects(sender, params) {
    params.list.forEach(report=>{
      try { var json = JSON.parse(report.output) } catch(e) { json = [] }
      this.updateChart(report.id, json)
    })
  } // mmObjects

  updateChart(chart_id, json) {
    const chart = this.TEMPLATES[chart_id]
    if(!chart) return
    chart.data = []
    let stats = this.mapStats(json, chart.type)
    if(!stats) return
    const transformation = 'transform_' + chart_id
    stats = typeof this[transformation] === 'function' ? this[transformation](stats) : stats
    if(stats.labels) {
      chart.labels.forEach(label=>{
        const idx = stats.labels.indexOf(label)
        const data = idx > -1 ? stats.data[idx] : 0
        chart.data.push(parseInt(data))
      })
    } else {
      for (const s in stats) {
        chart.data.push({
          label: LANG.translate(this.el.language, 'dashboard.'+chart_id+'.'+s),
          fill: false,
          borderColor: chart.colors[chart.labels.indexOf(s)],
          data: stats[s],
        })
      }
    }
    this.updateCharts(chart_id, chart)
  } // updateCharts

  updateCharts(key, src) {
    const type = this.TEMPLATES[key].type
    const chart = this.charts[key]
    let update_interval = {}
    if(!src.data || !chart) return
    if(type == 'line') {
      update_interval = { duration: 0 }
      chart.data.datasets = src.data
    } else if(type == 'doughnut') {
      chart.data.datasets.forEach((dataset) => {
        dataset.data = src.data
        dataset.backgroundColor = src.colors
      })
    }

    chart.update(update_interval)
  }

  initChart(key) {
    const el = this.elCharts.find('@{'+key+'}')
    if(!el) return

    const ctx = el.getContext('2d')
    const init_func = 'init_'+this.TEMPLATES[key].type+'_chart'
    if(typeof this[init_func] === 'function') {
      this.charts[key] = this[init_func](key, ctx)
    }
  }

  //TODO: update charts to accept single array data objects as input data

  // helper method to map all data to arrays usable in charts
  mapStats(data, type) {
    if(!data) return
    if(type == 'doughnut') {
      return { labels: Object.keys(data), data: Object.values(data) }
    }
  }

  init_line_chart(key, ctx) {
    return new Chart(ctx, {
      type: 'line',
      plugins: [],
      options: {
        title: {
          display: true,
          fontSize: 18,
          text: LANG.translate(this.el.language, 'dashboard.'+key),
        },
        responsive: false,
        plugins: {
          datalabels: {
            display: false,
          },
        },
        scales: {
          xAxes: [{
            type: 'time',
            display: true,
          }],
          yAxes: [{
            display: true,
          }],
        },
      },
    })
  }

  init_doughnut_chart(key, ctx) {
    return new Chart(ctx, {
      type: 'doughnut',
      plugins: [ChartDataLabels, doughnutlabel],
      data: {
        labels: this.TEMPLATES[key].labels.map(l=>LANG.translate(this.el.language, 'dashboard.'+key+'.'+l)),
        datasets: [{}],
      },
      options: {
        legend: { reverse: true },
        title: {
          display: true,
          fontSize: 18,
          text: LANG.translate(this.el.language, 'dashboard.'+key),
        },
        responsive: false,
        plugins: {
          datalabels: {
            backgroundColor: function(context) {
              return context.dataset.backgroundColor
            },
            borderColor: 'white',
            borderRadius: 50,
            borderWidth: 2,
            color: 'white',
            font: {
              weight: 'bold',
              size: 12,
            },
            display: function(context) {
              return context.dataset.data[context.dataIndex] > 0
            },
          },
          doughnutlabel: {
            labels: [
              {
                text: 'TOTAL',
                font: {
                  size: '15',
                },
              },
              {
                text: function(context) {
                  const sum = context.data.datasets[0].data.reduce((a, b) => a + b, 0)
                  return sum
                },
                font: {
                  size: '30',
                },
              },
            ],
          },
        },
      },
    })
  }

  transform_mpatrol_status(obj) {
    const renameLabel = function(status) {
      if(/bout|bpause/.test(status)) {
        status = 'bpause'
      } else if(/opatrol|oinstructions/.test(status)) {
        status = 'opatrol'
      } else if(/rbusy|rgoscene|ronscene/.test(status)) {
        status = 'rbusy'
      } else {
        status = 'gready'
      }
      return status
    }
    return this._renameLabel(obj, renameLabel)
  }

  _renameLabel(obj, renameFunc) {
    const labels = []; const data = []
    obj.labels.forEach((label, idx)=>{
      const new_label = renameFunc(label)
      const label_idx = labels.indexOf(new_label)
      if(label_idx > -1) {
        data[label_idx] += parseInt(obj.data[idx])
      } else {
        labels.push(new_label)
        data.push(parseInt(obj.data[idx]))
      }
    })
    return { labels, data }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by Window Manager when top full-screen window on any screen changes. Sends directly to that window and to #WM queue.
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.id]
  * @param {integer} [params.screen]
  */
  mmWindowFocused(sender, params) {
    this.window_active = !('id' in params) || params.id==this.listener_id
    this.watchReports()
  } // mmWindowFocused

  watchReports(force) {
    if(this.window_active && this.user_id>0) {
      if(!this.watching_dashboard || force) {
        this.watching_dashboard = true
        this.publish(this.config.workflow_server, 'mmWatch', { version: 0 })
      }
    }else{
      if(this.watching_dashboard || force) {
        this.watching_dashboard = false
        this.publish(this.config.workflow_server, 'mmWatch', {})
      }
    }
  } // watchReports
} // mmodOfficeWfDashboard
